import {
  CircularProgress,
  IconButton,
  TextField,
  withStyles,
} from "@material-ui/core";
import { ArrowDropDown, CloseOutlined } from "@material-ui/icons";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

const WIDTH_RESIZE = 1700; // same in ticket agent dashboard
const OPTIONS_HEIGHT = 300;
const OPTIONS_HEIGHT_RESIZE = 200;
const OPTION_MIN_HEIGHT = 45;

const styles = (theme) => {
  const FONT_SIZE = 16;
  const ICON_SIZE = 35;

  const OPTIONS_CONTAINER = {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow:
      "0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)",
    maxHeight: OPTIONS_HEIGHT,
    overflow: "auto",
    fontSize: FONT_SIZE,
  };

  const OPTION = {
    minHeight: OPTION_MIN_HEIGHT,
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.14) !important",
      cursor: "pointer",
    },
    padding: "5px 10px",
  };

  const OPTION_EMTYP = {
    height: "45px",
    display: "flex",
    alignItems: "center",
    padding: "5px 10px",
    color: "#bbb",
  };

  const CLEAR_BUTTON_SIZE = {
    width: ICON_SIZE,
    height: ICON_SIZE,
  };

  const OPTIONS_WRAPPER = {
    position: "absolute",
    backgroundColor: "transparent",
    zIndex: 9999,
    width: "100%",
  };

  return {
    textFieldRoot: {
      margin: "0px !important",
      "& input::-webkit-input-placeholder": {
        color: "rgba(0, 0, 0, 0.87)",
        opacity: 1,
      },
    },
    optionsWrapper: OPTIONS_WRAPPER,
    optionsContainer: OPTIONS_CONTAINER,
    optionsContainerResize: {
      ...OPTIONS_CONTAINER,
      [theme.breakpoints.down(WIDTH_RESIZE)]: {
        maxHeight: OPTIONS_HEIGHT_RESIZE,
        fontSize: FONT_SIZE - 2,
      },
    },
    option: OPTION,
    optionResize: {
      ...OPTION,
      [theme.breakpoints.down(WIDTH_RESIZE)]: {
        minHeight: OPTION_MIN_HEIGHT - 5,
      },
    },
    optionEmpty: OPTION_EMTYP,
    optionEmptyResize: {
      ...OPTION_EMTYP,
      [theme.breakpoints.down(WIDTH_RESIZE)]: {
        height: FONT_SIZE + 18,
        fontSize: FONT_SIZE - 2,
      },
    },
    adornedEnd: {
      padding: "0 2px 0 0",
    },
    clearButton: CLEAR_BUTTON_SIZE,
    clearButtonResize: {
      ...CLEAR_BUTTON_SIZE,
      [theme.breakpoints.down(WIDTH_RESIZE)]: {
        width: CLEAR_BUTTON_SIZE.width - 10,
        height: CLEAR_BUTTON_SIZE.height - 10,
      },
    },
    clearIconResize: {
      [theme.breakpoints.down(WIDTH_RESIZE)]: {
        width: CLEAR_BUTTON_SIZE.width - 10,
        height: CLEAR_BUTTON_SIZE.height - 10,
      },
    },
  };
};

const Autocomplete = ({
  options,
  onChangeValue, // callback khi chọn option
  onTextChange, // callback khi cần set lại list options, fire when text input change or clear input
  value,
  label,
  getLabel,
  isLoading,
  debounceTime, // thời gian delay khi tìm kiếm
  onScrollBottom, // callback khi scroll to bottom của list option
  onOpen = () => {}, // callback khi mở list option
  onClose = () => {}, // callback khi đóng list option
  enableResize, // resize responsive khi ở màn hình nhỏ
  classes,
  isVisibleAllOption, // true / false
  InputProps,
  ...otherProps
}) => {
  const [text, setText] = useState("");
  const isHoverListOptions = useRef(false);
  const [currentListOptions, setCurrentListOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const [openToTop, setOpenToTop] = useState(false);
  const [bottomPosition, setBottomPosition] = useState(0);
  const searchTextSroll = useRef("");

  useEffect(() => {
    setCurrentListOptions(options);
  }, [options]);

  useEffect(() => {
    setText(getLabel(value) || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleInputDelay = useCallback(
    debounce((searchText) => {
      if (onTextChange) {
        onTextChange(searchText);
      } else {
        setCurrentListOptions(
          options.filter((item) =>
            getLabel(item).toLowerCase().includes(searchText.toLowerCase())
          ) || []
        );
      }
    }, debounceTime || 0),
    [options]
  );

  const handleInputChange = (searchText) => {
    setText(searchText);
    searchTextSroll.current = searchText;
    handleInputDelay(searchText?.trim() || "");
  };

  const onScrollBottomDebounce = debounce(() => {
    onScrollBottom(text, currentListOptions?.length || 0);
  }, 200);

  const scrollHandler = (e) => {
    const bottom =
      Math.ceil(e.target.scrollTop) + e.target.clientHeight + 1 >=
      e.target.scrollHeight;

    if (bottom) {
      onScrollBottomDebounce();
      // onScrollBottom(text, currentListOptions?.length || 0);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <TextField
        classes={{ root: classes.textFieldRoot }}
        label={label}
        onChange={(e) => handleInputChange(e.target.value)}
        value={text}
        variant="outlined"
        fullWidth
        inputRef={inputRef}
        onFocus={() => {
          const { y, height } = inputRef.current.getBoundingClientRect();
          let currentHeightOptionsWrapper = OPTIONS_HEIGHT;
          if (enableResize && window.innerWidth < WIDTH_RESIZE) {
            currentHeightOptionsWrapper = OPTIONS_HEIGHT_RESIZE;
          }
          if (y + height + currentHeightOptionsWrapper > window.innerHeight) {
            setOpenToTop(true);
          } else {
            setOpenToTop(false);
          }
          setBottomPosition(height + 1);
          setCurrentListOptions(options);
          setOpen(true);
          onOpen();
        }}
        onBlur={() => {
          if (!isHoverListOptions.current) {
            setText(getLabel(value) || "");
            setOpen(false);
            onClose();
          }
        }}
        placeholder={isVisibleAllOption && !open ? "Tất cả" : ""}
        spellCheck="false"
        InputLabelProps={{
          shrink: value || open ? true : false,
        }}
        InputProps={{
          ...InputProps,
          classes: {
            adornedEnd: classes.adornedEnd,
            ...(InputProps?.classes?.input && {
              input: InputProps?.classes?.input,
            }),
          },
          endAdornment: (
            <>
              {isLoading && (
                <CircularProgress
                  style={{ width: 30, height: 27, marginRight: 10 }}
                />
              )}
              {!isLoading && open && (
                <IconButton
                  className={
                    enableResize
                      ? classes.clearButtonResize
                      : classes.clearButton
                  }
                  onClick={() => {
                    onChangeValue("");
                    if (onTextChange) onTextChange("");
                    setText("");
                    setOpen(false);
                    onClose();
                    isHoverListOptions.current = false;
                  }}
                  onMouseOver={() => {
                    isHoverListOptions.current = true;
                  }}
                  onMouseOut={() => {
                    isHoverListOptions.current = false;
                  }}>
                  <CloseOutlined
                    className={enableResize ? classes.clearIconResize : ""}
                  />
                </IconButton>
              )}
              {!isLoading && !open && <ArrowDropDown />}
            </>
          ),
        }}
        {...otherProps}
      />
      {open && (
        <div
          className={classes.optionsWrapper}
          style={openToTop ? { bottom: `${bottomPosition}px` } : null}>
          <div
            className={
              enableResize
                ? classes.optionsContainerResize
                : classes.optionsContainer
            }
            onMouseOver={() => {
              isHoverListOptions.current = true;
            }}
            onMouseOut={() => {
              isHoverListOptions.current = false;
            }}
            onScroll={onScrollBottom ? scrollHandler : null}>
            {currentListOptions?.map((item, index) => (
              <div
                key={index}
                onClick={() => {
                  onChangeValue(item);
                  setText(getLabel(item));
                  setOpen(false);
                  onClose();
                  isHoverListOptions.current = false;
                }}
                className={enableResize ? classes.optionResize : classes.option}
                style={{
                  backgroundColor:
                    getLabel(item) === getLabel(value)
                      ? "rgba(0, 0, 0, 0.14)"
                      : "#FFFFFF",
                }}>
                {getLabel(item)}
              </div>
            ))}
            {currentListOptions?.length === 0 && (
              <div
                className={
                  enableResize ? classes.optionEmptyResize : classes.optionEmpty
                }>
                Không có kết quả
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default withStyles(styles)(Autocomplete);
